import { ThemeColor } from '@/enums'
import { useInsuranceGroups } from '@/modules/insurances/hooks/useInsuranceGroups'
import { FormProvider, useFormItem, useSnackbar } from '@/providers'
import { useCreateMultipleInsurances } from '@/services'
import { Button } from '@surein/ui'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'
import { BackComponent, Typography } from '../../atoms'
import { Column } from '../../styled'
import { FormGroupCardWithSelectModal } from '../GroupCardSelectWithModal'
import { Container, PageActions } from './styles'
import { ValidationMessages, validationSchema } from './validationSchema'
import { JSX } from 'react'

interface Insurance {
  categoryId: string
  carrierId: string
}

interface AddInsuranceFormData {
  insurances: Insurance[]
}

interface OutletContextType {
  carriers: Array<{
    id: string
    name: string
  }>
}

interface Messages {
  back: string
  addInsurance: string
  addPolicy: string
  title: string
  prompt: string
  skip: string
  skipHint: string
  validations: ValidationMessages
  selectProvider: string
  selectProviderDescription: string
  change: string
  noInsuranceFound: string
  noInsuranceFoundDescription: string
  searchProvider: string
}

const useMessages = (search?: string): Messages => {
  const { t } = useTranslation()

  return {
    back: t('common.back'),
    addInsurance: t('locationPage.addInsurance'),
    addPolicy: t('addInsurance.addPolicy'),
    title: t('addInsurance.title'),
    prompt: t('addInsurance.prompt'),
    skip: t('addInsurance.skip'),
    skipHint: t('addInsurance.skipHint'),
    validations: t('validations'),
    selectProvider: t('selectProvider'),
    selectProviderDescription: t('selectProviderDescription'),
    change: t('change'),
    noInsuranceFound: t('yearlyAssessment.noInsuranceFound'),
    noInsuranceFoundDescription: t(
      'yearlyAssessment.noInsuranceFoundDescription',
      { search },
    ),
    searchProvider: t('searchProvider'),
  }
}

const AddInsuranceForm = (): JSX.Element => {
  const { value, onChange } = useFormItem('insurances')
  const { carriers } = useOutletContext<OutletContextType>()
  const messages = useMessages()
  const cardOptions = useInsuranceGroups()

  const mappedCarriers = carriers.map((carrier) => ({
    text: carrier.name,
    value: carrier.id,
  }))

  return (
    <>
      <FormGroupCardWithSelectModal
        cardKey="categoryId"
        cardOptions={cardOptions}
        listKey="carrierId"
        listOptions={mappedCarriers}
        modalSubtitle={messages.selectProviderDescription}
        modalTitle={messages.selectProvider}
        name="insurances"
        onChange={onChange}
        resetText={messages.change}
        searchPlaceholder={messages.searchProvider}
      />
      <PageActions>
        <Button
          disabled={!value?.length}
          type="submit"
          variant="primary"
          shape="square"
        >
          {messages.addPolicy}
        </Button>
      </PageActions>
    </>
  )
}

interface AddInsuranceViewProps {
  onBack?: () => void
  onSuccess: () => void
  locationId: string
  textColor?: ThemeColor
}

export const AddInsuranceView = ({
  onBack,
  onSuccess,
  locationId,
  textColor = ThemeColor.b0,
}: AddInsuranceViewProps): JSX.Element => {
  const { t } = useTranslation()
  const messages = useMessages()
  const snackbar = useSnackbar()
  const createMultipleInsurancesMutation = useCreateMultipleInsurances()

  const handleSubmit = async ({
    insurances,
  }: AddInsuranceFormData): Promise<void> => {
    try {
      await createMultipleInsurancesMutation
        .mutateAsync({ locationId, data: insurances })
        .then(onSuccess)
    } catch (_error) {
      snackbar.error(t('snackbars.error.generalMsg'))
    }
  }

  return (
    <Container>
      <Column gap="16px">
        <BackComponent noContentGutter onBack={onBack} />
        <Typography color={textColor} responsive variant="h3">
          {messages.title}
        </Typography>
        <Typography color={textColor} variant="captionR">
          {messages.prompt}
        </Typography>
      </Column>
      <FormProvider
        onSubmit={handleSubmit}
        validationSchema={validationSchema(messages.validations)}
      >
        <AddInsuranceForm />
      </FormProvider>
    </Container>
  )
}
